// ============================================================================
// <WorkflowTimeline> — append-only audit feed of one instance.
// ============================================================================
// Props:
//   instanceId   — the workflow instance whose history to render
// Synthetic events (__start__, __approval_requested__, __approval_rejected__,
// __approval_cancelled__) get distinct visual treatment.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { WorkflowHistory } from '../api/types';
import { useWorkflowInstance } from '../hooks/useWorkflowInstance';

interface Props {
  instanceId: string;
}

const isSynthetic = (code: string) => code.startsWith('__') && code.endsWith('__');

const eventLabel = (h: WorkflowHistory) => {
  switch (h.triggerCode) {
    case '__start__': return 'Workflow started';
    case '__approval_requested__': return 'Approval requested';
    case '__approval_rejected__': return 'Approval rejected';
    case '__approval_cancelled__': return 'Approval cancelled';
    case '__system__': return 'System event';
    default: return `Triggered: ${h.triggerCode}`;
  }
};

const eventColor = (code: string): string => {
  if (code === '__start__') return '#2563eb';
  if (code === '__approval_requested__') return '#d97706';
  if (code === '__approval_rejected__' || code === '__approval_cancelled__') return '#dc2626';
  if (isSynthetic(code)) return '#6b7280';
  return '#16a34a';
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export const WorkflowTimeline: React.FC<Props> = ({ instanceId }) => {
  const { data, loading, error } = useWorkflowInstance(instanceId);

  if (loading) {
    return <div style={{ padding: '1rem', color: 'var(--text-secondary)' }}>Loading timeline…</div>;
  }
  if (error || !data) {
    return <div style={{ padding: '1rem', color: 'var(--text-secondary)' }}>No history available.</div>;
  }

  const events = [...data.history].sort(
    (a, b) => new Date(b.firedAt).getTime() - new Date(a.firedAt).getTime(),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.5rem' }}>
      {events.length === 0 && (
        <div style={{ padding: '0.75rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          No events recorded yet.
        </div>
      )}
      {events.map((h) => (
        <div
          key={h.id}
          style={{
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'flex-start',
            padding: '0.625rem 0.875rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderLeft: `4px solid ${eventColor(h.triggerCode)}`,
            borderRadius: '6px',
          }}
        >
          <div style={{ marginTop: 2, color: eventColor(h.triggerCode) }}>
            {isSynthetic(h.triggerCode)
              ? <IconSystem.info size={16} />
              : <IconSystem.arrowRight size={16} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {eventLabel(h)}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {h.fromStateCode ? <span><strong>{h.fromStateCode}</strong> → </span> : null}
              <strong>{h.toStateCode}</strong>
              <span style={{ marginLeft: '0.5rem' }}>· {formatDate(h.firedAt)}</span>
              {h.actorUserName ? <span> · by {h.actorUserName}</span> : null}
            </div>
            {h.comment && (
              <div
                style={{
                  fontSize: '0.8125rem',
                  fontStyle: 'italic',
                  marginTop: '0.25rem',
                  color: 'var(--text-secondary)',
                }}
              >
                "{h.comment}"
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default WorkflowTimeline;
